import { keyframes, styled } from "goober";
import { type ReactElement, useEffect, useRef, useState } from "react";
import { ToastCard } from "./Toast";
import type { IconName } from "./Icon";
import { Button } from "./Button";
import { Tooltip } from "./Tooltip";

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-6px);
  }
`;

const Overlay = styled("div")`
  position: absolute;
  top: 0;
  left: calc(-1 * var(--workflow-chat-gutter, 12px));
  right: calc(-1 * var(--workflow-chat-gutter, 12px));
  padding: 12px;
  pointer-events: none;
  z-index: 1;

  &[data-placement="inline"] {
    position: relative;
    inset: auto;
    padding: 0;
    z-index: auto;
  }

  &.entering {
    animation: ${slideIn} 240ms cubic-bezier(0.22, 0.68, 0.35, 1.05);
  }
  &.exiting {
    animation: ${slideOut} 240ms cubic-bezier(0.22, 0.68, 0.35, 1.05) forwards;
  }
`;

const CardHolder = styled("div")`
  pointer-events: auto;
`;

const SecondaryButton = styled(Button)`
  &&[data-icon-only="true"] {
    width: 24px;
    padding: 4px;
    gap: 0;
    justify-content: center;

    [data-action-label] {
      display: none;
    }
  }
`;

export type ChatBannerProps = {
  show: boolean;
  icon: IconName | ReactElement;
  message: string | ReactElement;
  buttonLabel: string;
  secondaryAction?: {
    label: string;
    icon: IconName;
    onClick: () => void;
    disabled?: boolean;
    tooltip?: string;
  };
  disabled?: boolean;
  placement?: "overlay" | "inline";
  onButtonClick: () => void;
};

export function ChatBanner({
  show,
  icon,
  message,
  buttonLabel,
  secondaryAction,
  disabled = false,
  placement = "overlay",
  onButtonClick,
}: ChatBannerProps) {
  const [rendered, setRendered] = useState(show);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [iconOnly, setIconOnly] = useState(false);
  const hasSecondaryAction = !!secondaryAction;
  useEffect(() => {
    const card = cardRef.current;
    if (!rendered || !hasSecondaryAction || !card) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setIconOnly(entry.contentRect.width <= 400);
    });
    observer.observe(card);
    return () => observer.disconnect();
  }, [rendered, hasSecondaryAction]);
  // Latch rendered on so the banner stays mounted for its exit animation, which
  // clears it again on the overlay's onAnimationEnd below.
  if (show && !rendered) setRendered(true);
  if (!rendered) return null;
  return (
    <Overlay
      role="status"
      data-placement={placement}
      aria-hidden={!show}
      inert={!show || disabled}
      aria-busy={disabled}
      className={
        placement === "inline" ? undefined : show ? "entering" : "exiting"
      }
      onAnimationEnd={(e) => {
        if (e.currentTarget === e.target && !show) setRendered(false);
      }}
    >
      <CardHolder ref={cardRef}>
        <ToastCard
          fullWidth
          icon={icon}
          message={message}
          buttonLabel={buttonLabel}
          onButtonClick={onButtonClick}
          secondaryAction={
            secondaryAction && (
              <Tooltip
                hidden={!iconOnly}
                content={secondaryAction.tooltip ?? secondaryAction.label}
              >
                <SecondaryButton
                  data-icon-only={iconOnly}
                  variant="secondary"
                  size="m"
                  icon={secondaryAction.icon}
                  aria-label={secondaryAction.label}
                  disabled={secondaryAction.disabled}
                  onClick={secondaryAction.onClick}
                >
                  <span data-action-label>{secondaryAction.label}</span>
                </SecondaryButton>
              </Tooltip>
            )
          }
        />
      </CardHolder>
    </Overlay>
  );
}
